import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";

const CreatorLanding = lazy(() => import("./pages/CreatorLanding"));
const CreatorPortal = lazy(() => import("./pages/CreatorPortal"));
const KolPricingReference = lazy(() => import("./pages/KolPricingReference"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/** Legacy ?token=… email URLs → /portal/:token (preserves other query params, sets default tab). */
function LegacyTokenRedirect({ tab }: { tab: 'availability' | 'onboarding' | 'deliverables' }) {
  const [search] = useSearchParams();
  const token = search.get('token');
  if (!token) return <Navigate to="/" replace />;
  const next = new URLSearchParams(search);
  next.delete('token');
  if (!next.has('tab')) next.set('tab', tab);
  const qs = next.toString();
  return <Navigate to={`/portal/${token}${qs ? `?${qs}` : ''}`} replace />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<CreatorLanding />} />
            <Route path="/pricing" element={<KolPricingReference />} />
            <Route path="/portal/:token" element={<CreatorPortal />} />
            <Route path="/availability" element={<LegacyTokenRedirect tab="availability" />} />
            <Route path="/onboarding" element={<LegacyTokenRedirect tab="onboarding" />} />
            <Route path="/deliverables" element={<LegacyTokenRedirect tab="deliverables" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
